import React from 'react';

const COMPONENTS = [
  { name: 'Logistics Performance (LPI)', weight: 10, source: 'World Bank LPI', desc: 'Composite index measuring trade logistics quality (customs, infrastructure, tracking, timeliness)' },
  { name: 'Safe Water Access', weight: 9, source: 'WHO/UNICEF JMP', desc: 'Percentage of population with access to safely managed drinking water' },
  { name: 'Sanitation Access', weight: 8, source: 'WHO/UNICEF JMP', desc: 'Percentage of population with access to basic sanitation services' },
  { name: 'Electricity Access', weight: 9, source: 'World Bank WDI', desc: 'Percentage of population with access to electricity' },
  { name: 'Internet Penetration', weight: 8, source: 'ITU / World Bank', desc: 'Percentage of population using the internet' },
  { name: 'Tertiary Education', weight: 8, source: 'UNESCO', desc: 'Gross enrollment ratio in tertiary education' },
  { name: 'Bank Account Penetration', weight: 7, source: 'Global Findex', desc: 'Percentage of adults (15+) with a bank account' },
  { name: 'CAPCI', weight: 7, source: 'Calculated', desc: 'Credit-Augmented Per Capita Income: GDP_pc × (1 + 0.435 × FAM), where FAM = DTI/100' },
  { name: 'Grassroots Credit Ratio', weight: 6, source: 'Calculated', desc: 'GCR = Household_Debt / (Population × GDP_pc) — measures household credit access relative to income' },
  { name: 'Rule of Law', weight: 6, source: 'World Bank WGI', desc: 'Perceptions of confidence in societal rules, contract enforcement, property rights, courts' },
  { name: 'Real Wage Growth', weight: 6, source: 'ILO / UNSTAT', desc: 'RealWage = Nominal_Wage_Index / Price_Index — inflation-adjusted wage purchasing power' },
  { name: 'Insurance Penetration', weight: 5, source: 'Swiss Re Sigma', desc: 'Total insurance premiums as percentage of GDP' },
  { name: 'Control of Corruption', weight: 5, source: 'World Bank WGI', desc: 'Perceptions of extent to which public power is exercised for private gain' },
  { name: 'Physicians per 1,000', weight: 6, source: 'WHO / World Bank', desc: 'Number of medical doctors per 1,000 population' },
];

const sty = {
  page: { maxWidth: 800, margin: '0 auto', padding: '2rem 1.5rem 4rem' },
  h1: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '2rem', color: '#0f172a', marginBottom: '0.5rem' },
  h2: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.4rem', color: '#0f172a', marginTop: '2.5rem', marginBottom: '0.75rem' },
  p: { color: '#475569', lineHeight: 1.8, marginBottom: '1rem', fontSize: '0.95rem' },
  formula: { background: '#0f172a', color: '#5eead4', padding: '1.2rem 1.5rem', borderRadius: 10, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', lineHeight: 1.7, margin: '1rem 0', overflowX: 'auto', whiteSpace: 'pre-wrap' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', margin: '1rem 0' },
  th: { padding: '0.6rem 0.75rem', textAlign: 'left', background: '#f8fafc', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0' },
  td: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  note: { background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: '1rem 1.2rem', margin: '1rem 0', fontSize: '0.9rem', color: '#0f766e' },
};

export default function MethodologyPage() {
  return (
    <div style={sty.page}>
      <h1 style={sty.h1}>Methodology</h1>
      <p style={sty.p}>
        The Macro-Governance Index (MGI) is a 14-component weighted index measuring governance
        infrastructure and development capacity. It is derived from the theoretical framework in
        <em>Macro-Governance Theory: A Quantitative Framework for Measuring and Enhancing Jurisdictional Wellbeing</em>.
      </p>

      <h2 style={sty.h2}>MGI Formula</h2>
      <p style={sty.p}>
        MGI is computed as a weighted sum of 14 normalized percentile values. Each country is
        ranked against all others for each metric, and the percentile position (0–100) is used
        as the component score.
      </p>
      <div style={sty.formula}>
{`MGI = Σ (Component_Percentile × Weight)

Where weights (sum to 1.00):
  LPI=0.10, Water=0.09, Sanitation=0.08, Electricity=0.09,
  Internet=0.08, Education=0.08, Bank=0.07, CAPCI=0.07,
  GCR=0.06, RuleOfLaw=0.06, RealWage=0.06, Insurance=0.05,
  CorruptionControl=0.05, Physicians=0.06
  
  Missing components: rescale available weights to sum to 1.0`}
      </div>

      <div style={sty.note}>
        <strong>Missing Component Handling:</strong> When a country lacks data for some metrics,
        the weights of available components are rescaled to sum to 1.0. This ensures comparable
        MGI scores across countries with different data availability.
        <br /><br />
        <code>reweighted = original_weight / Σ(available_weights)</code>
      </div>

      <h2 style={sty.h2}>14 MGI Components</h2>
      <table style={sty.table}>
        <thead>
          <tr>
            <th style={sty.th}>#</th>
            <th style={sty.th}>Component</th>
            <th style={{ ...sty.th, textAlign: 'center' }}>Weight</th>
            <th style={sty.th}>Source</th>
          </tr>
        </thead>
        <tbody>
          {COMPONENTS.map((c, i) => (
            <tr key={i}>
              <td style={{ ...sty.td, fontFamily: "'JetBrains Mono', monospace", color: '#94a3b8' }}>{i + 1}</td>
              <td style={sty.td}>
                <strong>{c.name}</strong>
                <br />
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{c.desc}</span>
              </td>
              <td style={{ ...sty.td, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                {(c.weight).toFixed(0)}%
              </td>
              <td style={{ ...sty.td, fontSize: '0.8rem', color: '#64748b' }}>{c.source}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={sty.h2}>Key Formulas</h2>

      <h3 style={{ ...sty.h2, fontSize: '1.1rem', marginTop: '1.5rem' }}>CAPCI (Credit-Augmented Per Capita Income)</h3>
      <div style={sty.formula}>
{`CAPCI = GDP_Per_Capita × (1 + α × FAM)

Where:
  α = 0.435 (verified coefficient)
  FAM = DTI / 100 (Finance Multiplier)
  DTI = Debt-to-Income ratio (%)

Example (USA 2023):
  GDP_pc = $85,000, DTI = 82%
  FAM = 82/100 = 0.82
  CAPCI = $85,000 × (1 + 0.435 × 0.82) = $115,320`}
      </div>

      <h3 style={{ ...sty.h2, fontSize: '1.1rem', marginTop: '1.5rem' }}>GCR (Grassroots Credit Ratio)</h3>
      <div style={sty.formula}>
{`GCR = (Household_Debt_USD / Population) / GDP_Per_Capita

Measures household credit access relative to income.
Higher GCR = more grassroots financial access.

Example (USA 2023):
  HH Debt = ~$57,938M, Pop = 335M, GDP_pc = $85,000
  GCR = (57938/335M) / 85000 ≈ 0.61`}
      </div>

      <h3 style={{ ...sty.h2, fontSize: '1.1rem', marginTop: '1.5rem' }}>Real Wage Growth</h3>
      <div style={sty.formula}>
{`RealWage = Nominal_Wage_Index / Price_Index

Measures inflation-adjusted wage purchasing power.
Value > 1.0 = real wages increased.`}
      </div>

      <h2 style={sty.h2}>Percentile Normalization</h2>
      <p style={sty.p}>
        All component values are converted to percentile rankings (0–100) by comparing each
        country against all others for that metric in the same year:
      </p>
      <div style={sty.formula}>
{`Percentile = (Rank / Total_Countries_With_Data) × 100

Higher raw value → higher percentile (for "higher is better" metrics)
Lowest → ~0th percentile
Highest → ~100th percentile`}
      </div>

      <h2 style={sty.h2}>Data Coverage</h2>
      <p style={sty.p}>
        The dataset covers 170 countries across 26 years (2000–2025). Data is sourced from
        World Bank WDI, UNESCO, ILO, WHO/UNICEF, Swiss Re, BIS, and national central banks.
        Blue+bold formatting in the source workbook indicates interpolated or gap-filled values.
        All data is publicly available and sourced from official international organizations.
      </p>

      <div style={sty.note}>
        This tool is non-political and purely data-driven. The MGI measures governance infrastructure
        capacity — it does not endorse or criticize any government. All source data is freely
        available in the public domain.
      </div>
    </div>
  );
}
